import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import CloseIcon from '@mui/icons-material/Close'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IPersonSkillOrIndustryOrRole } from 'types/cvsInterfaces'
import { ISkillLevel } from 'types/levelItemInterfaces'
import DeleteButton from 'Components/reusable/IconButtons/DeleteButton'
import SkillsItem from '../SkillsItem'
import { TableInputField } from 'Components/reusable/InputFields/TableInputField'
import InterestLevelPicker from 'Components/reusable/InputFields/InterestLevelPicker'
import LevelPicker from 'Components/reusable/InputFields/LevelPicker'
import CaleoIconButton from 'Components/reusable/IconButtons/CaleoIconButton'
import DoneIcon from '@mui/icons-material/Done'
import { ExperienceInputField } from 'Components/reusable/InputFields/ExperienceInputField'
import colors from 'constants/colors'
import { EditableFields, ErrorFields } from 'types/skillsTableInterfaces'
import { toPositiveInteger } from 'utils/utils'
import { Schema, ValidationError } from 'yup'

interface IEditRowProps {
  editableSkill: IPersonSkillOrIndustryOrRole
  editable: boolean
  type: string
  onSubmit: (newSkill: IPersonSkillOrIndustryOrRole) => void
  errors: ErrorFields<EditableFields<IPersonSkillOrIndustryOrRole>>
  setErrors: (value: React.SetStateAction<ErrorFields<EditableFields<IPersonSkillOrIndustryOrRole>>>) => void
  setDeleteConnectedSkillConfirm: React.Dispatch<React.SetStateAction<boolean>>
  setEditableSkill: React.Dispatch<React.SetStateAction<IPersonSkillOrIndustryOrRole | undefined>>
  schema: Schema<unknown, object>
}

const EditRow: React.FC<IEditRowProps> = ({
  editableSkill,
  editable,
  type,
  onSubmit,
  errors,
  setErrors,
  setDeleteConnectedSkillConfirm,
  setEditableSkill,
  schema,
}) => {
  const { t } = useTranslation()

  const [currentSkill, setCurrentSkill] = useState<IPersonSkillOrIndustryOrRole>(editableSkill)

  useEffect(() => {
    const parseValidationErrors = (err: unknown) => {
      if (!(err instanceof ValidationError)) {
        throw err
      }

      const newErrors: typeof errors = {}

      for (const inner of err.inner) {
        const path = inner.path
        if (path) {
          newErrors[path as keyof typeof newErrors] = inner.message
        }
      }

      setErrors(newErrors)
    }

    ;(async () => {
      try {
        await schema.validate(currentSkill, { abortEarly: false })
        setErrors({})
      } catch (error) {
        parseValidationErrors(error)
        return error
      }
    })()
  }, [currentSkill, schema, setErrors])

  return (
    <TableRow
      sx={{
        borderTop: `1px solid ${colors.borderColor}`,
        borderBottom: `1px solid ${colors.borderColor}`,
      }}
    >
      <TableCell>
        <SkillsItem
          skill={currentSkill}
          editable={editable}
          showTooltip={true}
          key={currentSkill.id}
          showLastUsed={true}
          type={type}
        />
      </TableCell>
      <TableCell>
        <LevelPicker
          kind="skill"
          value={currentSkill?.level ?? null}
          onChange={newValue => {
            if (currentSkill && newValue) setCurrentSkill({ ...currentSkill, level: newValue as ISkillLevel })
          }}
          smallIcons={true}
          disabled={!currentSkill?.skillId}
        />
      </TableCell>
      <TableCell>
        <InterestLevelPicker
          smallIcons={true}
          value={currentSkill?.interestLevel ?? null}
          onChange={newValue => {
            if (currentSkill && newValue) setCurrentSkill({ ...currentSkill, interestLevel: newValue })
          }}
          disabled={!currentSkill?.skillId}
        />
      </TableCell>
      <TableCell>
        <ExperienceInputField
          value={currentSkill?.experienceMonths ?? 0}
          onChange={(newValue: number) => {
            if (currentSkill && newValue)
              setCurrentSkill({ ...currentSkill, experienceMonths: toPositiveInteger(newValue) })
          }}
          disabled={!currentSkill?.skillId}
          error={errors['experienceMonths']}
        />
      </TableCell>
      <TableCell>
        <TableInputField
          value={currentSkill?.lastUsed ?? 2022}
          onChange={newValue => {
            if (currentSkill && newValue)
              setCurrentSkill({ ...currentSkill, lastUsed: toPositiveInteger(Number(newValue)) })
          }}
          type="number"
          helperText={t(`profile.${type}.year`)}
          disabled={!currentSkill?.skillId}
          error={errors['lastUsed']}
        />
      </TableCell>
      <TableCell>
        <CaleoIconButton
          testId={`button-skills-edit-row-save-${currentSkill?.id ?? currentSkill?.skillId ?? 'new'}`}
          size="medium"
          clickAction={() => {
            onSubmit(currentSkill)
          }}
          icon={<DoneIcon />}
          tooltip={t('save')}
          valid={!!currentSkill?.skillId}
        />
        {!!currentSkill?.id && (
          <DeleteButton
            testId={`button-skills-edit-row-delete-${currentSkill.id}`}
            clickAction={() => {
              setDeleteConnectedSkillConfirm(true)
            }}
            size="medium"
          />
        )}
        <CaleoIconButton
          testId={`button-skills-edit-row-cancel-${currentSkill?.id ?? currentSkill?.skillId ?? 'new'}`}
          size="medium"
          clickAction={() => {
            setEditableSkill(undefined)
          }}
          icon={<CloseIcon />}
          tooltip={t('close')}
        />
      </TableCell>
    </TableRow>
  )
}

export default EditRow
